import { translate } from '@teamleader/i18n';
import { AnimationItem } from 'lottie-web';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { useIsExtraLarge, useIsHuge, useIsSmall } from '../../../../../Components/Context/Hooks/mediaQueries';
import useLogoPosition from '../../../../../Components/Context/Hooks/useLogoPosition';
import Box from '../../../../../Components/UI/Box';
import Branding from '../../../../../Components/UI/Branding';
import Lottie from '../../../../../Components/UI/Lottie';
import MobilePopup from '../../../../../Components/UI/MobilePopup';
import OverflowingFlexBox from '../../../../../Components/UI/OverflowingFlexBox';
import Sidebar, { SIZES } from '../../../../../Components/UI/Sidebar';
import Document from '../../../../../Svg/120x120_document.svg';
import paying from '../../../../../Svg/Lottie/paying.json';
import { CheckoutStatus } from '../../../../../Types/api';
import { MATCHED_STATUS } from '../../../../../Types/invoice';
import { PAYMENT_STATUS } from '../../../../../Types/payment';
import { MENUS } from '../../../../../Utils/Menus';
import { canOpenNavigationForInvoice } from '../../../../../Utils/canOpenNavigation';
import useGlobalContext, { InvoiceContext } from '../../../Context';
import { SEARCH_PARAMS } from '../../../Home';
import ErrorMessage from '../../ErrorMessage';
import SuccessMessage from '../../SuccessMessage';
import * as styles from '../../styles.css';

const REFRESH_INTERVAL = 1000;
const PDF_REGENERATION_GRACE_PERIOD = 2000;
const FINISHED_STATUSES = [PAYMENT_STATUS.COMPLETED, PAYMENT_STATUS.FAILED, PAYMENT_STATUS.DETACHED];

const InvoicePaymentStatusMenu: React.FC = () => {
  const { handleCloseMenu, handleOpenMenu, type } = useGlobalContext();
  const {
    invoiceId,
    invoice,
    invoicePdf,
    openInvoices,
    paidInvoices,
    creditedInvoices,
    clearCache,
    setMadeTooManyPaymentAttempts,
  } = useContext(InvoiceContext);
  const animation = useRef<AnimationItem>(null);
  const isSmall = useIsSmall();
  const isExtraLarge = useIsExtraLarge();
  const isHuge = useIsHuge();
  const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.NOT_STARTED);
  const { inSuccessMessage } = useLogoPosition(
    canOpenNavigationForInvoice(openInvoices?.data, paidInvoices?.data, creditedInvoices?.data),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const Element = isSmall ? MobilePopup : Sidebar;

  const isInvoiceLoaded = !!invoice && !!invoicePdf;
  const isInvoicePaid = invoice?.matchedStatus === MATCHED_STATUS.FULLY_PAID;
  const args = useMemo(
    (): [string, RequestInit] => ['/checkout.status', { body: JSON.stringify({ invoice_id: invoiceId }) }],
    [invoiceId],
  );
  const { data } = useSWR<CheckoutStatus>(args, {
    refreshInterval: REFRESH_INTERVAL,
    isPaused: () => !isInvoiceLoaded || FINISHED_STATUSES.includes(paymentStatus),
  });

  const handleRetry = useCallback(() => {
    handleOpenMenu!(MENUS.PAYMENT);
  }, [handleOpenMenu]);

  useEffect(() => {
    (async () => {
      if (!data) {
        return;
      }

      const paymentStatus = data.data.status;

      if (paymentStatus === PAYMENT_STATUS.COMPLETED) {
        searchParams.delete(SEARCH_PARAMS.OPEN_MENU);
        setSearchParams(searchParams);

        await new Promise((resolve) => window.setTimeout(resolve, PDF_REGENERATION_GRACE_PERIOD));
        void clearCache!();
      }

      await new Promise<void>((resolve) => {
        if (!animation.current) {
          resolve();
        }

        animation.current!.addEventListener('loopComplete', () => resolve());
      });
      animation.current?.removeEventListener('loopComplete');

      setPaymentStatus(paymentStatus);

      if (paymentStatus === PAYMENT_STATUS.NOT_STARTED) {
        handleOpenMenu!(MENUS.PAYMENT);
      }

      if (paymentStatus === PAYMENT_STATUS.FAILED || paymentStatus === PAYMENT_STATUS.DETACHED) {
        searchParams.delete(SEARCH_PARAMS.OPEN_MENU);
        setSearchParams(searchParams);

        if (!data.data.retryAllowed) {
          setMadeTooManyPaymentAttempts!(true);
        }
      }
    })();
  }, [data, handleOpenMenu, clearCache, searchParams, setSearchParams, setMadeTooManyPaymentAttempts]);

  if (!invoice) {
    return null;
  }

  return (
    <Element
      className={styles['menu']}
      display="flex"
      flexDirection="column"
      element="form"
      onClose={FINISHED_STATUSES.includes(paymentStatus) ? handleCloseMenu : undefined}
      size={isHuge ? SIZES.WIDEST : isExtraLarge ? SIZES.WIDER : undefined}
    >
      <OverflowingFlexBox>
        <Box paddingBottom={4} paddingHorizontal={4} className={styles['container']}>
          <Box
            flex={1}
            flexDirection="column"
            alignItems="center"
            className={styles['message']}
            paddingHorizontal={4}
            display={FINISHED_STATUSES.includes(paymentStatus) || isInvoicePaid ? 'none' : 'flex'}
          >
            <Lottie ref={animation} animationData={paying} loop play className={styles['message-icon']}>
              <Document />
            </Lottie>
            <h2 className={styles['message-title']}>{translate('paymentStatusSidebar.processingMessageTitle')}</h2>
            <p className={styles['message-description']}>{translate('paymentStatusSidebar.processingMessageText')}</p>
          </Box>
          {(paymentStatus === PAYMENT_STATUS.COMPLETED || isInvoicePaid) && (
            <SuccessMessage
              title={translate('paymentStatusSidebar.paidMessageTitle')}
              description={translate('paymentStatusSidebar.paidMessageText', {
                departmentName: invoice.department.name,
              })}
              icon={<Document />}
              branding={
                inSuccessMessage ? (
                  <Branding language={invoice.client.language} country={invoice.client.country} type={type} />
                ) : undefined
              }
            />
          )}
          {paymentStatus === PAYMENT_STATUS.FAILED && (
            <ErrorMessage
              title={translate('paymentStatusSidebar.errorMessageTitle')}
              description={translate('paymentStatusSidebar.errorMessageText')}
              icon={<Document />}
              onRetry={handleRetry}
              retryLabel={data?.data.retryAllowed ? undefined : translate('tooltips.tooManyAttempts')}
            />
          )}
          {paymentStatus === PAYMENT_STATUS.DETACHED && (
            <SuccessMessage
              title={translate('paymentStatusSidebar.detachedMessageTitle')}
              description={translate('paymentStatusSidebar.detachedMessageText', {
                departmentName: invoice.department.name,
              })}
              icon={<Document />}
              branding={
                inSuccessMessage ? (
                  <Branding language={invoice.client.language} country={invoice.client.country} type={type} />
                ) : undefined
              }
            />
          )}
        </Box>
      </OverflowingFlexBox>
    </Element>
  );
};

export default InvoicePaymentStatusMenu;
